// src/services/looker/lookerTemplateAccess.ts
/**
 * Which Looker templates a workspace has on /looker (migration 110): the one
 * default every workspace gets (looker_template_settings) plus the templates
 * a platform master granted it (looker_workspace_templates). RLS lets only a
 * master write, so a refused write comes back as false, never as a success.
 *
 * @module services/looker/lookerTemplateAccess
 */
import { getSupabaseClient } from '@/config/supabase';
import { logError } from '@/services/logger';
import { TemplateSpecSchema, type TemplateSpec } from './lookerTemplateSpec';

export interface WorkspaceTemplateAccess {
  defaultId: string | null;
  granted: string[];
}

export type WorkspaceTemplateAccessResult = { ok: true; data: WorkspaceTemplateAccess } | { ok: false };

export const fetchWorkspaceTemplateAccess = async (workspaceId: string): Promise<WorkspaceTemplateAccessResult> => {
  const client = getSupabaseClient();
  const [settings, grants] = await Promise.all([
    client.from('looker_template_settings').select('default_template_id').maybeSingle(),
    client.from('looker_workspace_templates').select('template_id').eq('workspace_id', workspaceId),
  ]);
  if (settings.error || grants.error) {
    logError('LookerTemplateAccess', 'access read failed', settings.error ?? grants.error);
    return { ok: false };
  }
  return {
    ok: true,
    data: { defaultId: settings.data?.default_template_id ?? null, granted: (grants.data ?? []).map((g) => g.template_id) },
  };
};

export const grantLookerTemplate = async (workspaceId: string, templateId: string): Promise<boolean> => {
  const { data, error } = await getSupabaseClient()
    .from('looker_workspace_templates')
    .upsert({ workspace_id: workspaceId, template_id: templateId }, { onConflict: 'workspace_id,template_id' })
    .select('template_id');
  if (error || !data || data.length === 0) {
    logError('LookerTemplateAccess', 'grant failed', error ?? new Error('no row written'));
    return false;
  }
  return true;
};

export const revokeLookerTemplate = async (workspaceId: string, templateId: string): Promise<boolean> => {
  const { error } = await getSupabaseClient()
    .from('looker_workspace_templates')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('template_id', templateId);
  if (error) {
    logError('LookerTemplateAccess', 'revoke failed', error);
    return false;
  }
  return true;
};

/** The one template every workspace has. */
export const setDefaultLookerTemplate = async (templateId: string): Promise<boolean> => {
  const { data, error } = await getSupabaseClient()
    .from('looker_template_settings')
    .upsert({ lock: true, default_template_id: templateId }, { onConflict: 'lock' })
    .select('default_template_id');
  if (error || !data || data.length === 0) {
    logError('LookerTemplateAccess', 'default change failed', error ?? new Error('no row written'));
    return false;
  }
  return true;
};

export interface TemplateGrant {
  workspaceId: string;
  templateId: string;
}

/** One grant as a set member, for "does this workspace have this template?". */
export const grantKey = (workspaceId: string, templateId: string): string => `${workspaceId}:${templateId}`;

export type TemplateGrantsResult = { ok: true; data: TemplateGrant[] } | { ok: false };

const GRANTS_PAGE = 1000;

/**
 * Every grant in every workspace (a master's read; RLS shows anyone else only
 * their own). Paged: PostgREST stops at 1000 rows with HTTP 200 and no error,
 * and a short read would show granted templates as not granted. Ordered by the
 * primary key, so pages never overlap or skip. A failed page fails the whole read.
 */
export const fetchAllTemplateGrants = async (): Promise<TemplateGrantsResult> => {
  const client = getSupabaseClient();
  const rows: TemplateGrant[] = [];
  for (let from = 0; ; from += GRANTS_PAGE) {
    const { data, error } = await client
      .from('looker_workspace_templates')
      .select('workspace_id, template_id')
      .order('workspace_id', { ascending: true })
      .order('template_id', { ascending: true })
      .range(from, from + GRANTS_PAGE - 1);
    if (error) {
      logError('LookerTemplateAccess', 'grants read failed', error);
      return { ok: false };
    }
    const page = data ?? [];
    rows.push(...page.map((g) => ({ workspaceId: g.workspace_id, templateId: g.template_id })));
    if (page.length < GRANTS_PAGE) return { ok: true, data: rows };
  }
};

/**
 * The builder spec of a template made in the app (looker_app_templates,
 * migration 112), read on the master's own token — RLS returns nothing to
 * anyone else. Null when there is no row (made in Looker) or it no longer
 * parses; 'error' when the read failed.
 */
export const fetchAppTemplateSpec = async (templateId: string): Promise<TemplateSpec | null | 'error'> => {
  const { data, error } = await getSupabaseClient()
    .from('looker_app_templates')
    .select('spec')
    .eq('template_id', templateId)
    .maybeSingle();
  if (error) {
    logError('LookerTemplateAccess', 'app template read failed', error);
    return 'error';
  }
  const parsed = TemplateSpecSchema.safeParse(data?.spec);
  return parsed.success ? parsed.data : null;
};
